import JSZip from 'jszip';
import type { ActivityResut, ActivityRawData } from '@/types/activity';
import type { RTPDownloadResult } from '@/types/rtpDownload';
import { buildRTPCsvRows, buildRTPCsvString, RTP_CSV_HEADERS } from '@/utils/rtpCsv';

/**
 * Interface for patient activity data from API
 */
interface PatientActivityData {
    username: string;
    result: ActivityResut[];
}

interface PatientRawActivityData {
    username: string;
    result: ActivityRawData[];
}

/**
 * Create a ZIP file from activity data organized by patient and activity folders
 * 
 * Structure:
 * nushu-activities-YYYY-MM-DD.zip
 * ├── p-patient1/
 * │   ├── activity-abc123/
 * │   │   └── activity.json
 * │   └── activity-def456/
 * │       └── activity.json
 * └── p-patient2/
 *     └── activity-ghi789/
 *         └── activity.json
 */
export async function createActivityZip(
    data: PatientActivityData[] | unknown
): Promise<Blob> {
    const zip = new JSZip();
    const normalized = normalizeActivityDownloadData(data);

    // Process each patient's data
    for (const patientData of normalized) {
        const patientFolder = formatPatientFolder(patientData.username);

        // Process each activity for this patient
        for (const activity of patientData.result) {
            const activityFolder = formatActivityFolder(getActivityId(activity));
            const folderPath = `${patientFolder}/${activityFolder}`;
            const activityPayload = {
                meta: activity.meta ?? {},
                macro: Array.isArray(activity.macro) && activity.macro.length === 0
                    ? {}
                    : (activity.macro ?? {}),
                results: activity.results ?? {},
            };

            zip.file(
                `${folderPath}/activity.json`,
                JSON.stringify(activityPayload, null, 2)
            );
        }
    }

    // Generate the ZIP file
    return await zip.generateAsync({ type: 'blob' });
}

/**
 * Create a ZIP file from raw activity data organized by patient and activity folders
 *
 * Structure:
 * nushu-activities-raw-YYYY-MM-DD.zip
 * ├── p-patient1/
 * │   └── activity-abc123/
 * │       └── raw_data.json
 */
export async function createActivityRawZip(
    data: PatientRawActivityData[] | unknown
): Promise<Blob> {
    const zip = new JSZip();
    const normalized = normalizeActivityRawDownloadData(data);

    for (const patientData of normalized) {
        const patientFolder = formatPatientFolder(patientData.username);

        for (const activity of patientData.result) {
            const activityFolder = formatActivityFolder(activity.activity_id || 'activity-unknown');
            const folderPath = `${patientFolder}/${activityFolder}`;

            zip.file(
                `${folderPath}/raw_data.json`,
                JSON.stringify(activity.raw_data, null, 2)
            );
        }
    }

    return await zip.generateAsync({ type: 'blob' });
}

/**
 * Format patient folder name from username
 * Ensures consistent naming and handles special characters
 */
function formatPatientFolder(username: string): string {
    // Remove any path-unsafe characters and use the username as-is
    return username.replace(/[/\\?%*:|"<>]/g, '-');
}

/**
 * Format activity folder name from activity ID
 * Ensures consistent naming and handles special characters
 */
function formatActivityFolder(activityId: string): string {
    // Remove any path-unsafe characters
    return activityId.replace(/[/\\?%*:|"<>]/g, '-');
}

function formatCsvFilename(value: string): string {
    return value.replace(/[/\\?%*:|"<>]/g, '-');
}

/**
 * Create a ZIP file for a single patient's activities
 * Used by patient download (when downloading their own activities)
 */
export async function createSinglePatientZip(
    activities: ActivityResut[],
    username: string
): Promise<Blob> {
    // Convert to the same format as multi-patient download
    const data: PatientActivityData[] = [
        {
            username,
            result: activities,
        },
    ];

    return createActivityZip(data);
}

export async function createSinglePatientRawZip(
    activities: ActivityRawData[],
    username: string
): Promise<Blob> {
    const data: PatientRawActivityData[] = [
        {
            username,
            result: activities,
        },
    ];

    return createActivityRawZip(data);
}

export async function createRtpCsvZip(results: RTPDownloadResult[]): Promise<Blob> {
    const zip = new JSZip();

    results.forEach((result) => {
        const rows = buildRTPCsvRows(result);
        const csv = buildRTPCsvString(RTP_CSV_HEADERS, rows);
        const patientLabel = result.patientName || result.patientId;
        const filename = `${formatCsvFilename(patientLabel)}.csv`;
        zip.file(filename, csv);
    });

    return await zip.generateAsync({ type: 'blob' });
}

function normalizeActivityDownloadData(data: PatientActivityData[] | unknown): PatientActivityData[] {
    if (!data) return [];

    if (Array.isArray(data)) {
        if (data.length === 0) return [];

        const first = data[0] as Record<string, unknown>;

        if (first && typeof first === 'object' && ('result' in first || 'results' in first)) {
            return (data as Array<Record<string, unknown>>).map((item) => ({
                username: String(
                    item.username ?? item.user_id ?? item.userId ?? 'unknown'
                ),
                result: (item.result ?? item.results ?? []) as ActivityResut[],
            }));
        }

        if (first && typeof first === 'object' && ('activity_id' in first || 'results' in first)) {
            return [
                {
                    username: 'unknown',
                    result: data as ActivityResut[],
                },
            ];
        }

        return [];
    }

    if (typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        if (Array.isArray(obj.data)) {
            return normalizeActivityDownloadData(obj.data);
        }
        if ('result' in obj || 'results' in obj) {
            return [
                {
                    username: String(
                        obj.username ?? obj.user_id ?? obj.userId ?? 'unknown'
                    ),
                    result: (obj.result ?? obj.results ?? []) as ActivityResut[],
                },
            ];
        }
    }

    return [];
}

function normalizeActivityRawDownloadData(data: PatientRawActivityData[] | unknown): PatientRawActivityData[] {
    if (!data) return [];

    if (Array.isArray(data)) {
        if (data.length === 0) return [];

        const first = data[0] as Record<string, unknown>;

        if (first && typeof first === 'object' && ('result' in first || 'results' in first)) {
            return (data as Array<Record<string, unknown>>).map((item) => ({
                username: String(
                    item.username ?? item.user_id ?? item.userId ?? 'unknown'
                ),
                result: (item.result ?? item.results ?? []) as ActivityRawData[],
            }));
        }

        if (first && typeof first === 'object' && ('activity_id' in first || 'raw_data' in first)) {
            return [
                {
                    username: 'unknown',
                    result: data as ActivityRawData[],
                },
            ];
        }

        return [];
    }

    if (typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        if (Array.isArray(obj.data)) {
            return normalizeActivityRawDownloadData(obj.data);
        }
        if ('result' in obj || 'results' in obj) {
            return [
                {
                    username: String(
                        obj.username ?? obj.user_id ?? obj.userId ?? 'unknown'
                    ),
                    result: (obj.result ?? obj.results ?? []) as ActivityRawData[],
                },
            ];
        }
    }

    return [];
}

function getActivityId(activity: ActivityResut): string {
    const fromResults = (activity.results as { id?: string } | undefined)?.id;
    const fromMeta = (activity.meta as { id?: string } | undefined)?.id;
    return activity.activity_id || fromResults || fromMeta || 'activity-unknown';
}
